import Avatar from '@mui/material/Avatar';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { styled, useTheme, alpha } from '@mui/material/styles';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import Facebook from '@mui/icons-material/Facebook';
import Group from '@mui/icons-material/Group';
import Instagram from '@mui/icons-material/Instagram';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import ReplayIcon from '@mui/icons-material/Replay';
import Sms from '@mui/icons-material/Sms';
import Telegram from '@mui/icons-material/Telegram';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import WhatsApp from '@mui/icons-material/WhatsApp';
import {
	format,
	isSameDay,
	parseISO
} from "date-fns";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	useNavigate,
	useParams
} from "react-router-dom";
import receiveIcon from "../../assets/receive.png";
import sendIcon from "../../assets/send.png";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import AcceptTicketWithouSelectQueue from "../AcceptTicketWithoutQueueModal";
import ConfirmationModal from "../ConfirmationModal";
import openSocket from "../../services/socket-io";
import WhatsMarked from "react-whatsmarked";

const StyledListItem = styled(ListItem)(({ theme }) => ({
	position: "relative",
	cursor: "pointer",
	borderRadius: '10px',
	margin: '3px 6px',
	padding: '8px 10px 8px 14px',
	minHeight: '62px',
	transition: 'all 0.18s ease',
	// CC_TEMA_ESTUDIO_NOITE: cartao grafite (antes azulado #1e2a3a)
	backgroundColor: theme.palette.mode === 'dark'
		? '#1A1C20'
		: '#ffffff',
	border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(25,118,210,0.08)'}`,
	boxShadow: theme.palette.mode === 'dark'
		? '0 1px 3px rgba(0,0,0,0.3)'
		: '0 1px 4px rgba(25,118,210,0.06)',
	'&.Mui-selected': {
		backgroundColor: theme.palette.mode === 'dark' ? '#25292E' : alpha('#F2A93B', 0.08),
		border: `1px solid ${alpha('#F2A93B', 0.45)}`,
		boxShadow: `inset 3px 0 0 #F2A93B`,
	},
	'&:hover': {
		backgroundColor: theme.palette.mode === 'dark' ? '#202328' : alpha('#000000', 0.03),
		border: `1px solid rgba(255,255,255,0.10)`,
		boxShadow: 'none',
	},
	'&.Mui-selected:hover': {
		backgroundColor: theme.palette.mode === 'dark' ? '#272B31' : alpha('#F2A93B', 0.1),
	}
}));

const AvatarContainer = styled(ListItemAvatar)(({ theme }) => ({
	position: "relative",
	minWidth: '48px',
	marginRight: theme.spacing(1),
}));

const TicketAvatar = styled(Avatar)(({ theme }) => ({
	width: "42px",
	height: "42px",
	borderRadius: "10px",
	border: 'none',
	fontWeight: 700,
	fontSize: '0.85rem',
	transition: 'box-shadow 0.2s ease',
	boxShadow: theme.palette.mode === 'dark' ? '0 2px 6px rgba(0,0,0,0.3)' : '0 1px 4px rgba(0,0,0,0.12)',
}));

const AVATAR_COLORS = ['#1e88e5','#00897b','#7b1fa2','#e53935','#f57c00','#558b2f','#0288d1','#6a1b9a','#2e7d32','#c0392b'];

const getAvatarColor = (name) => {
  if (!name) return '#1e88e5';
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return parts[0] ? parts[0].substring(0, 2).toUpperCase() : '?';
};

const UnreadBadgeDot = styled('span')(({ theme }) => ({
	position: 'absolute',
	bottom: 1,
	right: 3,
	minWidth: 16,
	height: 16,
	borderRadius: 8,
	backgroundColor: '#25D366',
	color: '#04210F',
	fontFamily: '"IBM Plex Mono", monospace',
	fontSize: '0.6rem',
	fontWeight: 700,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
	padding: '0 3px',
}));

const GroupBadge = styled(Badge)(({ theme }) => ({
	position: "absolute",
	top: 0,
	left: 0,
	'& .MuiBadge-badge': {
		backgroundColor: '#1565c0',
		minWidth: '14px',
		height: '14px',
		borderRadius: '7px',
		boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
	}
}));

const TagBadge = styled(Badge)(({ theme }) => ({
	position: "absolute",
	bottom: 0,
	left: 28,
	'& .MuiBadge-badge': {
		backgroundColor: '#ff6d00',
		minWidth: '14px',
		height: '14px',
		borderRadius: '7px',
		boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
		cursor: 'pointer',
	}
}));

const ClosedBadge = styled(Badge)(({ theme }) => ({
	alignSelf: "center",
	marginRight: 48,
	marginLeft: "auto",
	'& .MuiBadge-badge': {
		backgroundColor: alpha('#78909c', 0.15),
		color: '#78909c',
		fontSize: '0.6rem',
		fontWeight: 700,
		borderRadius: 4,
		border: '1px solid rgba(120,144,156,0.3)',
	}
}));

const ActionBtn = styled(IconButton)(({ theme, btncolor }) => ({
	padding: 5,
	borderRadius: 8,
	backgroundColor: alpha(btncolor || '#1976d2', 0.08),
	color: btncolor || '#1976d2',
	transition: 'all 0.15s ease',
	'& .MuiSvgIcon-root': {
		fontSize: '1rem'
	},
	'&:hover': {
		backgroundColor: alpha(btncolor || '#1976d2', 0.18),
		transform: 'scale(1.08)',
	}
}));

const ButtonContainer = styled('div')(({ theme }) => ({
	display: "flex",
	flexDirection: 'column',
	justifyContent: "center",
	alignItems: "flex-end",
	gap: theme.spacing(0.5),
	marginLeft: 'auto',
	paddingLeft: theme.spacing(0.75),
}));

const TicketQueueColor = styled('span')(({ color }) => ({
	flex: "none",
	width: "4px",
	height: "60%",
	position: "absolute",
	top: '20%',
	left: 0,
	borderRadius: '0 3px 3px 0',
	backgroundColor: color || '#78909c',
}));

const InlineTagChip = styled('span')(({ color }) => ({
	display: 'inline-flex',
	alignItems: 'center',
	fontSize: '0.58rem',
	fontWeight: 700,
	padding: '1px 5px',
	borderRadius: 20,
	backgroundColor: color ? alpha(color, 0.13) : 'rgba(25,118,210,0.1)',
	color: color || '#1976d2',
	border: `1px solid ${color ? alpha(color, 0.28) : 'rgba(25,118,210,0.25)'}`,
	letterSpacing: '0.2px',
	textTransform: 'uppercase',
	lineHeight: '14px',
	whiteSpace: 'nowrap',
	maxWidth: 90,
	overflow: 'hidden',
	textOverflow: 'ellipsis',
}));

const TicketListItem = ({ ticket, filteredTags }) => {
	const theme = useTheme();
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [loading, setLoading] = useState(false);
	const { ticketId } = useParams();
	const isMounted = useRef(true);
	const { user } = useContext(AuthContext);
	const [acceptTicketWithouSelectQueueOpen, setAcceptTicketWithouSelectQueueOpen] = useState(false);
	const [confirmationOpen, setConfirmationOpen] = useState(false);
	const [tag, setTag] = useState([]);
	const [settings, setSettings] = useState([]);
	const [spy, setSpy] = useState([]);
	const [currentTicket, setCurrentTicket] = useState(ticket);
	const defaultImage = '/default-profile.png';

	useEffect(() => {
		setCurrentTicket(ticket);
	}, [ticket.id, ticket.lastMessage, ticket.unreadMessages, ticket.status, ticket.userId]);

	useEffect(() => {
		const socket = openSocket();
		if (!socket) return;

		const handleAppMessage = (data) => {
			if (data.action === "create" || data.action === "update") {
				if (data.ticket && data.ticket.id === ticket.id) {
					setCurrentTicket(prevTicket => ({
						...prevTicket,
						...data.ticket,
						lastMessage: data.ticket.lastMessage || prevTicket.lastMessage,
						unreadMessages: data.ticket.unreadMessages !== undefined ? data.ticket.unreadMessages : prevTicket.unreadMessages
					}));
				}
			}
		};

		const handleContactUpdate = (data) => {
			if (data.action === "update" && data.contact) {
				if (data.contact.id === ticket.contactId || data.contact.id === ticket.contact?.id) {
					if (data.contact.tags) {
						setTag(data.contact.tags);
					}
				}
			}
		};

		socket.on("appMessage", handleAppMessage);
		socket.on("contact", handleContactUpdate);

		return () => {
			socket.off("appMessage", handleAppMessage);
			socket.off("contact", handleContactUpdate);
		};
	}, [ticket.id, ticket.contactId, ticket.contact]);

	useEffect(() => {
		isMounted.current = true;

		setTimeout(() => {
			const fetchTicket = async () => {
				try {
					const { data } = await api.get("/tickets/" + ticket.id);
					if (isMounted.current) {
						setTag(data?.contact?.tags);
					}
				} catch (err) {
					if (isMounted.current) {
						toastError(err, t);
					}
				}
			};
			fetchTicket();
		}, 500);

		return () => {
			isMounted.current = false;
		};
	}, [ticket.id, user, navigate, t]);

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	useEffect(() => {
		const fetchSettings = async () => {
		  try {
			const { data } = await api.get("/settings");
			setSettings(data);
			setSpy(data);
		  } catch (err) {
			console.error(err);
			toastError(err);
			setSettings([]);
			setSpy([]);
		  }
		};
		fetchSettings();
	  }, []);

	const filterTicketByTags = () => {
		if (!filteredTags || filteredTags.length === 0) return true;
		if (!tag || tag.length === 0) return false;
		return filteredTags.every(filterTag => tag.some(t => t.id === filterTag.id));
	};

	if (!filterTicketByTags()) {
		return null;
	}

	const handleAcceptTicket = async (id) => {
		setLoading(true);
		try {
			await api.put(`/tickets/${id}`, { status: "open", userId: user?.id });
			const isGroup = ticket?.contact?.isGroup || currentTicket?.contact?.isGroup;
			localStorage.setItem("pressticket:changeTab", isGroup ? "groups" : "open");
			navigate(`/tickets/${id}`);
		} catch (err) {
			if (isMounted.current) toastError(err);
		} finally {
			if (isMounted.current) setLoading(false);
		}
	};

	const queueName = selectedTicket => {
		let name = null;
		let color = null;
		user?.queues?.forEach(userQueue => {
			if (userQueue.id === selectedTicket.queueId) { name = userQueue.name; color = userQueue.color; }
		});
		return { name, color };
	};

	const handleOpenAcceptTicketWithouSelectQueue = () => setAcceptTicketWithouSelectQueueOpen(true);

	const handleReopenTicket = async (id) => {
		setLoading(true);
		try {
			await api.put(`/tickets/${id}`, { status: "open", userId: user?.id });
			const isGroup = ticket?.contact?.isGroup || currentTicket?.contact?.isGroup;
			localStorage.setItem("pressticket:changeTab", isGroup ? "groups" : "open");
			navigate(`/tickets/${id}`);
		} catch (err) {
			toastError(err);
		} finally {
			if (isMounted.current) setLoading(false);
		}
	};

	const handleViewTicket = async (id) => {
		setLoading(true);
		try {
			localStorage.setItem("pressticket:changeTab", "pending");
			navigate(`/tickets/${id}`);
		} catch (err) {
			toastError(err);
		} finally {
			if (isMounted.current) setLoading(false);
		}
	};

	const handleMoveTicket = async (id) => {
		setLoading(true);
		try {
			await api.put(`/tickets/${id}`, { status: "pending", userId: null });
			localStorage.setItem("pressticket:changeTab", "open");
			navigate(`/tickets`);
		} catch (err) {
			toastError(err);
		} finally {
			if (isMounted.current) setLoading(false);
		}
	};

	const handleClosedTicket = async (id) => {
		setLoading(true);
		try {
			await api.put(`/tickets/${id}`, { status: "closed", userId: user?.id, queueId: ticket?.queueId || null });
			window.dispatchEvent(new CustomEvent('pressticket:ticketClosed', { detail: { ticketId: id } }));
			navigate("/tickets");
		} catch (err) {
			toastError(err, t);
		} finally {
			if (isMounted.current) setLoading(false);
		}
	};

	const handleSelectTicket = id => navigate(`/tickets/${id}`);
	const handleOpenConfirmationModal = () => setConfirmationOpen(true);
	const handleConfirmClose = () => handleClosedTicket(ticket.id);

	const canTabsSettings = (ts) => (
		(settings && settings.some(s => s.key === ts && s.value === "enabled")) ||
		(user && user.profile === "admin")
	);

	const canSpy = (cs) => (
		(spy && spy.some(s => s.key === cs && s.value === "enabled")) ||
		(user && user.profile === "admin")
	);

	const queueColor = ticket.queue?.color || queueName(ticket)?.color || '#78909c';

	const channelIcon = (() => {
		if (ticket.contact.telegramId) return { icon: <Telegram sx={{ fontSize: '0.8rem', color: '#0088cc' }} />, label: 'Telegram' };
		if (ticket.contact.messengerId) return { icon: <Facebook sx={{ fontSize: '0.8rem', color: '#1877F2' }} />, label: 'Facebook' };
		if (ticket.contact.instagramId) return { icon: <Instagram sx={{ fontSize: '0.8rem', color: '#E1306C' }} />, label: 'Instagram' };
		if (ticket.contact.webchatId) return { icon: <Sms sx={{ fontSize: '0.8rem', color: '#ff7043' }} />, label: 'Webchat' };
		if (ticket.contact.number) return { icon: <WhatsApp sx={{ fontSize: '0.8rem', color: '#25D366' }} />, label: 'WhatsApp' };
		return null;
	})();

	const timeLabel = ticket.whatsappId
		? isSameDay(parseISO(ticket.updatedAt), new Date())
			? format(parseISO(ticket.updatedAt), "HH:mm")
			: format(parseISO(ticket.updatedAt), "dd/MM")
		: null;

	const lastMsgClean = currentTicket.lastMessage
		? currentTicket.lastMessage.replace(/🢇|🢅/g, '').trim()
		: '';

	const msgArrow = (() => {
		if (!currentTicket.lastMessage) return null;
		if (currentTicket.lastMessage.includes("🢅")) return <img src={sendIcon} alt="" width="8px" style={{ flexShrink: 0, opacity: 0.5 }} />;
		if (currentTicket.lastMessage.includes("🢇")) return <img src={receiveIcon} alt="" width="8px" style={{ flexShrink: 0, opacity: 0.5 }} />;
		return null;
	})();

	const isPending = ticket.status === "pending";
	const rightOffset = isPending ? 68 : 44;

	return (
		<React.Fragment key={ticket.id}>
			<AcceptTicketWithouSelectQueue
				modalOpen={acceptTicketWithouSelectQueueOpen}
				onClose={() => setAcceptTicketWithouSelectQueueOpen(false)}
				ticketId={ticket.id}
			/>
			<StyledListItem
				dense
				button
				onClick={() => handleSelectTicket(ticket.id)}
				selected={ticketId && +ticketId === ticket.id}
			>
				<Tooltip arrow placement="right" title={ticket.queue?.name || queueName(ticket)?.name || t("ticketsList.items.queueless")}>
					<TicketQueueColor color={queueColor} />
				</Tooltip>

				<AvatarContainer>
					<TicketAvatar
						src={ticket?.contact?.profilePicUrl || undefined}
						alt="contact_image"
						sx={!ticket?.contact?.profilePicUrl ? { bgcolor: getAvatarColor(ticket?.contact?.name), color: '#fff' } : {}}
					>
						{getInitials(ticket?.contact?.name)}
					</TicketAvatar>
					{ticket.unreadMessages > 0 && (
						<UnreadBadgeDot>{ticket.unreadMessages > 99 ? '99+' : ticket.unreadMessages}</UnreadBadgeDot>
					)}
					{ticket.isGroup && (
						<GroupBadge
							badgeContent={<Group sx={{ fontSize: '0.7rem' }} />}
							anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
							color="primary"
							overlap="circular"
						/>
					)}
					{tag && tag.length > 0 && (
						<Tooltip
							placement="right"
							arrow
							title={
								<Box sx={{ p: 1, minWidth: 140 }}>
									{tag.map((tg, i) => (
										<Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.4 }}>
											<Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: tg.color, flexShrink: 0 }} />
											<Typography sx={{ fontSize: '0.8rem' }}>{tg.name}</Typography>
										</Box>
									))}
								</Box>
							}
							componentsProps={{
								tooltip: {
									sx: {
										bgcolor: 'background.paper', color: 'text.primary', boxShadow: 3,
										border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1,
										'& .MuiTooltip-arrow': { color: 'background.paper' }
									}
								}
							}}
						>
							<TagBadge
								badgeContent={<LocalOfferIcon sx={{ fontSize: '0.6rem' }} />}
								anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
								overlap="circular"
							/>
						</Tooltip>
					)}
				</AvatarContainer>

				<ListItemText
					disableTypography
					sx={{ my: 0, overflow: 'hidden', pr: `${rightOffset + 4}px` }}
					primary={
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
							{channelIcon && (
								<Tooltip title={channelIcon.label} arrow placement="top">
									<Box sx={{ display: 'flex', flexShrink: 0 }}>{channelIcon.icon}</Box>
								</Tooltip>
							)}
							<Typography noWrap sx={{
								fontSize: '0.82rem', fontWeight: 700,
								color: theme.palette.mode === 'dark' ? '#ECEDEF' : '#1a2640',
								lineHeight: 1.3,
							}}>
								{ticket.contact.name}
							</Typography>
							{ticket.status === "closed" && (
								<ClosedBadge overlap="rectangular" badgeContent="fechado" max={999999} />
							)}
						</Box>
					}
					secondary={
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
								{msgArrow}
								<Typography noWrap sx={{
									fontSize: '0.71rem',
									color: theme.palette.mode === 'dark' ? '#A7ADB5' : 'rgba(30,50,80,0.48)',
									lineHeight: 1.3,
								}}>
									{lastMsgClean || <span style={{ fontStyle: 'italic', opacity: 0.4 }}>—</span>}
								</Typography>
							</Box>

							<Box sx={{ display: 'flex', alignItems: 'center', gap: '3px', flexWrap: 'nowrap', overflow: 'hidden' }}>
								{ticket.whatsappId && ticket.whatsapp?.name && (
									<InlineTagChip color={ticket.whatsapp?.color}>
										{ticket.whatsapp.name.slice(0, 13).toUpperCase()}
									</InlineTagChip>
								)}
								{ticket.status !== "pending" && ticket?.user?.name && (
									<InlineTagChip color="#1565c0">
										{ticket.user.name.split(' ')[0].toUpperCase()}
									</InlineTagChip>
								)}
								{tag && tag.slice(0, 2).map((tg, i) => (
									<InlineTagChip key={i} color={tg.color}>{tg.name.slice(0, 13).toUpperCase()}</InlineTagChip>
								))}
								{tag && tag.length > 2 && (
									<InlineTagChip color="#7b8ea0">+{tag.length - 2}</InlineTagChip>
								)}
							</Box>
						</Box>
					}
				/>

				{timeLabel && (
					<Typography sx={{
						position: 'absolute', right: rightOffset + 6, top: 8,
						fontSize: '0.66rem', fontWeight: 500, whiteSpace: 'nowrap', fontFamily: '"IBM Plex Mono", monospace', fontVariantNumeric: 'tabular-nums',
						color: theme.palette.mode === 'dark' ? '#767C85' : 'rgba(30,50,80,0.38)',
					}}>
						{timeLabel}
					</Typography>
				)}

				<ButtonContainer>
					{isPending && (ticket.queue === null || ticket.queue === undefined) && (
						<Tooltip title={t("ticketsList.items.accept")} placement="left" arrow>
							<ActionBtn btncolor="#00c853" onClick={() => handleOpenAcceptTicketWithouSelectQueue()}>
								<CheckCircleOutlineIcon />
							</ActionBtn>
						</Tooltip>
					)}
					{isPending && ticket.queue !== null && (
						<Tooltip title={t("ticketsList.items.accept")} placement="left" arrow>
							<ActionBtn btncolor="#00c853" onClick={() => handleAcceptTicket(ticket.id)}>
								<CheckCircleOutlineIcon />
							</ActionBtn>
						</Tooltip>
					)}
					{canSpy("listItemSpy") && isPending && (
						<Tooltip title={t("ticketsList.items.spy")} placement="left" arrow>
							<ActionBtn btncolor="#7c4dff" onClick={() => handleViewTicket(ticket.id)}>
								<VisibilityOutlinedIcon />
							</ActionBtn>
						</Tooltip>
					)}
					{isPending && (
						<Tooltip title={t("ticketsList.items.close")} placement="left" arrow>
							<ActionBtn btncolor="#f44336" onClick={handleOpenConfirmationModal}>
								<CloseIcon />
							</ActionBtn>
						</Tooltip>
					)}
					{canTabsSettings("tabsPending") && ticket.status === "open" && (
						<Tooltip title={t("ticketsList.items.return")} placement="left" arrow>
							<ActionBtn btncolor="#ff9800" onClick={() => handleMoveTicket(ticket.id)}>
								<ReplayIcon />
							</ActionBtn>
						</Tooltip>
					)}
					{ticket.status === "open" && (
						<Tooltip title={t("ticketsList.items.close")} placement="left" arrow>
							<ActionBtn btncolor="#f44336" onClick={handleOpenConfirmationModal}>
								<CloseIcon />
							</ActionBtn>
						</Tooltip>
					)}
					{ticket.status === "closed" && (
						<ActionBtn btncolor="#1976d2" onClick={() => handleReopenTicket(ticket.id)}>
							<ReplayIcon />
						</ActionBtn>
					)}
				</ButtonContainer>
			</StyledListItem>
			<ConfirmationModal
				title={t("tickets.confirmationModal.closeTicket.title")}
				open={confirmationOpen}
				onClose={() => setConfirmationOpen(false)}
				onConfirm={handleConfirmClose}
			>
				{t("tickets.confirmationModal.closeTicket.message")}
			</ConfirmationModal>
		</React.Fragment>
	);
};

export default TicketListItem;
